/**
 * Cookie-Based Auth Client
 *
 * FOS-5.6.7: logout / clear-sensitive-data over oracle-bridge.
 *
 * bl-1442-e: the session-READ surface (`checkSession`, `getSessionState`, the
 * in-memory `sessionState` store, and the `isAccessTokenExpired` /
 * `isAuthenticated` / `getUserId` estimators it backed) was removed. dao-suite
 * runs in `identity-jwt` mode (dac-001-7): the authenticated verdict and the
 * member's `ic_principal` come from the identity-service JWT (via `useAuth()` /
 * `getOwnIcPrincipal()`), never from an oracle-bridge cookie `/session` fetch, so
 * those reads had no live callers and never populated. `logout` is retained —
 * it still POSTs oracle-bridge `/api/auth/logout` and clears local sensitive data
 * (bl-1442-d owns the eventual logout/session-teardown cutover).
 *
 * Usage:
 * ```typescript
 * import { logout } from './authCookieClient';
 * await logout(); // clears cookies + all sensitive local data
 * ```
 */

import { csrfFetch, ensureCSRFToken } from '../utils/csrf';
import { clearAllSensitiveData } from '../utils/securityClear';

// =============================================================================
// Configuration
// =============================================================================

/**
 * Get the oracle-bridge base URL from environment
 * Defaults to same-origin (for proxy setup) or localhost in dev
 */
function getBaseUrl(): string {
  // VITE_ORACLE_BRIDGE_URL for explicit config
  if (import.meta.env.VITE_ORACLE_BRIDGE_URL) {
    return import.meta.env.VITE_ORACLE_BRIDGE_URL;
  }

  // In production, oracle-bridge is typically proxied on same origin
  if (import.meta.env.PROD) {
    return '';
  }

  // Development default
  return 'http://localhost:3000';
}

// =============================================================================
// Logout
// =============================================================================

/**
 * Logout - clear cookies, session, and all sensitive data
 *
 * FOS-5.6.7: Enhanced logout to clear all sensitive data
 * @see AC-5.6.7.6 - Logout clears all sensitive data from localStorage
 */
export async function logout(): Promise<void> {
  const baseUrl = getBaseUrl();

  try {
    // Ensure we have a CSRF token for the logout request
    await ensureCSRFToken(`${baseUrl}/api/auth`);

    // Call server to invalidate session
    await csrfFetch(`${baseUrl}/api/auth/logout`, {
      method: 'POST',
    });
  } catch (error) {
    // Ignore errors - continue with local cleanup
    if (import.meta.env.DEV) {
      console.warn('[authCookieClient] Logout request failed:', error);
    }
  }

  // FOS-5.6.7: Clear all sensitive data from localStorage/sessionStorage
  // This includes auth tokens, PII (email, name), and session markers
  clearAllSensitiveData();
}
